import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { CircularProgress } from "@material-ui/core";
import { FaStar, FaCheckCircle, FaRegCommentDots } from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi";
import ServiceProvider from "../../../api/ServiceProvider.js";

const CLIENT_UUID_KEY = "feedback_client_uuid";

function getOrCreateClientUuid() {
  let uuid = localStorage.getItem(CLIENT_UUID_KEY);
  if (!uuid) {
    uuid = (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(CLIENT_UUID_KEY, uuid);
  }
  return uuid;
}

function StarRating({ value, onChange, size = 30 }) {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: "flex", gap: "6px" }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <FaStar
          key={n}
          size={size}
          onClick={() => onChange(n)}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          style={{
            cursor: "pointer",
            color: n <= (hover || value) ? "#f59e0b" : "#e2e8f0",
            transition: "color 0.12s, transform 0.12s",
            transform: n <= (hover || value) ? "scale(1.08)" : "scale(1)",
          }}
        />
      ))}
    </div>
  );
}

function YesNoToggle({ value, onChange }) {
  const Btn = ({ v, label }) => (
    <button
      type="button"
      onClick={() => onChange(v)}
      style={{
        flex: 1,
        padding: "0.7rem",
        borderRadius: "10px",
        border: value === v ? "2px solid #a00030" : "1.5px solid #e2e8f0",
        background: value === v ? "linear-gradient(135deg,#a00030,#c0392b)" : "#fff",
        color: value === v ? "#fff" : "#64748b",
        fontWeight: 700,
        fontSize: "0.9rem",
        cursor: "pointer",
        transition: "all 0.15s",
      }}
    >
      {label}
    </button>
  );
  return (
    <div style={{ display: "flex", gap: "10px" }}>
      <Btn v={true} label="Yes" />
      <Btn v={false} label="No" />
    </div>
  );
}

export default function ServiceProviderFeedBackForm() {
  const { token } = useParams();

  const [feedbackId, setFeedbackId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [overallRating, setOverallRating] = useState(0);
  const [comment, setComment] = useState("");
  const [clientName, setClientName] = useState("");

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const loadForm = useCallback(() => {
    if (!token) {
      setLoadError("Invalid feedback link.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError(null);

    ServiceProvider.getForm(token)
      .then((res) => {
        const payload = res?.data;
        if (!payload || payload.status !== 1) {
          setLoadError(payload?.message || "This feedback link is invalid or no longer available.");
          return;
        }
        setFeedbackId(payload.data.feedbackId);
        setQuestions(payload.data.questions || []);
      })
      .catch(() => setLoadError("Could not load this feedback form. Please try again."))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    loadForm();
  }, [loadForm]);

  const setAnswer = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const buildAnswerPayload = () => {
    return questions
      .filter((q) => answers[q.id] !== undefined && answers[q.id] !== "" && answers[q.id] !== null)
      .map((q) => {
        const base = { questionId: q.id };
        if (q.type === "RATING") return { ...base, ratingValue: answers[q.id] };
        if (q.type === "YES_NO") return { ...base, yesNoValue: answers[q.id] };
        return { ...base, textValue: answers[q.id] };
      });
  };

  const validate = () => {
    if (overallRating === 0) return "Please give an overall rating.";

    const missingMandatory = questions.filter(
      (q) => q.mandatory && (answers[q.id] === undefined || answers[q.id] === "" || answers[q.id] === null)
    );
    if (missingMandatory.length > 0) {
      return `Please answer: ${missingMandatory.map((q) => q.question).join(", ")}`;
    }
    return null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationError = validate();
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    const payload = {
      clientUuid: getOrCreateClientUuid(),
      clientName: clientName.trim() || undefined,
      overallRating,
      comment: comment.trim() || undefined,
      answers: buildAnswerPayload(),
    };

    ServiceProvider.submit(token, payload)
      .then((res) => {
        const payloadRes = res?.data;
        if (!payloadRes || payloadRes.status !== 1) {
          setSubmitError(payloadRes?.message || "Could not submit feedback. Please try again.");
          return;
        }
        setSubmitted(true);
      })
      .catch((err) => {
        const msg = err?.response?.data?.message;
        setSubmitError(msg || "Could not submit feedback. Please try again.");
      })
      .finally(() => setSubmitting(false));
  };

  // Completion progress across overall rating + all questions, for the progress bar
  const progress = useMemo(() => {
    const totalFields = 1 + questions.length; // overall rating + each question
    let filled = overallRating > 0 ? 1 : 0;
    questions.forEach((q) => {
      if (answers[q.id] !== undefined && answers[q.id] !== "" && answers[q.id] !== null) filled += 1;
    });
    return Math.round((filled / totalFields) * 100);
  }, [overallRating, answers, questions]);

  const pageShell = (children) => (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(160deg,#fff7f7 0%,#fef2f2 35%,#f8fafc 100%)",
        padding: "2.5rem 1.25rem",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
      }}
    >
      {children}
    </div>
  );

  if (loading) {
    return pageShell(
      <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={30} style={{ color: "#a00030" }} />
      </div>
    );
  }

  if (loadError) {
    return pageShell(
      <div style={{ maxWidth: "440px", width: "100%", marginTop: "3rem", textAlign: "center" }}>
        <div
          style={{
            background: "#fff",
            border: "1px solid #fda4af",
            borderRadius: "18px",
            padding: "2rem 1.75rem",
            boxShadow: "0 12px 32px rgba(160,0,48,0.08)",
          }}
        >
          <div style={{ fontSize: "2rem", marginBottom: "0.75rem" }}>😕</div>
          <p style={{ color: "#c0392b", fontWeight: 700, margin: 0 }}>{loadError}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return pageShell(
      <div style={{ maxWidth: "440px", width: "100%", marginTop: "3rem", textAlign: "center" }}>
        <div
          style={{
            background: "#fff",
            border: "1px solid #6ee7b7",
            borderRadius: "20px",
            padding: "2.5rem 2rem",
            boxShadow: "0 16px 40px rgba(30,132,73,0.1)",
          }}
        >
          <div
            style={{
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              background: "#ecfdf5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 1.25rem",
            }}
          >
            <FaCheckCircle size={32} style={{ color: "#1e8449" }} />
          </div>
          <h2 style={{ color: "#0f172a", margin: "0 0 0.5rem", fontSize: "1.4rem", fontWeight: 900 }}>Thank you!</h2>
          <p style={{ color: "#64748b", margin: 0, fontSize: "0.95rem" }}>
            Your feedback has been submitted successfully. We really appreciate you taking the time.
          </p>
        </div>
      </div>
    );
  }

  return pageShell(
    <div style={{ maxWidth: "600px", width: "100%" }}>
      <div
        style={{
          background: "#fff",
          border: "1px solid #f1f5f9",
          borderRadius: "22px",
          overflow: "hidden",
          boxShadow: "0 20px 50px rgba(15,23,42,0.06)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "2rem 2rem 1.5rem",
            background: "linear-gradient(135deg,#a00030 0%,#c0392b 55%,#e11d48 100%)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "-40px",
              right: "-40px",
              width: "160px",
              height: "160px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.08)",
            }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
            <HiOutlineSparkles size={16} color="#fecdd3" />
            <span style={{ fontSize: "0.7rem", fontWeight: 800, color: "#fecdd3", letterSpacing: "0.14em", textTransform: "uppercase" }}>
              Share Your Experience
            </span>
          </div>
          <h1 style={{ margin: 0, fontSize: "1.5rem", fontWeight: 900, color: "#fff" }}>Feedback Form</h1>
          <p style={{ margin: "6px 0 0", fontSize: "0.85rem", color: "rgba(255,255,255,0.85)" }}>
            Your feedback helps us serve you better.
          </p>
        </div>

        {/* Progress bar */}
        <div style={{ padding: "1rem 2rem 0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
            <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#94a3b8" }}>Progress</span>
            <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#a00030" }}>{progress}%</span>
          </div>
          <div style={{ height: "6px", borderRadius: "10px", background: "#f1f5f9", overflow: "hidden" }}>
            <div
              style={{
                height: "100%",
                width: `${progress}%`,
                background: "linear-gradient(90deg,#a00030,#e11d48)",
                borderRadius: "10px",
                transition: "width 0.25s ease",
              }}
            />
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "1.5rem 2rem 2rem" }}>
          {submitError && (
            <div
              style={{
                color: "#c0392b",
                background: "#fff1f2",
                border: "1px solid #fda4af",
                borderRadius: "10px",
                padding: "0.75rem 1rem",
                marginBottom: "1.5rem",
                fontSize: "0.85rem",
                fontWeight: 600,
              }}
            >
              {submitError}
            </div>
          )}

          {/* Overall rating — hero section */}
          <div
            style={{
              background: "#fffbeb",
              border: "1px solid #fde68a",
              borderRadius: "16px",
              padding: "1.25rem 1.5rem",
              marginBottom: "1.5rem",
              textAlign: "center",
            }}
          >
            <label style={{ display: "block", fontSize: "0.95rem", fontWeight: 800, color: "#334155", marginBottom: "0.75rem" }}>
              How would you rate your overall experience? <span style={{ color: "#c0392b" }}>*</span>
            </label>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <StarRating value={overallRating} onChange={setOverallRating} size={34} />
            </div>
          </div>


          {/* Dynamic questions */}
          {questions.map((q, idx) => (
            <div
              key={q.id}
              style={{
                marginBottom: "1.25rem",
                padding: "1.1rem 1.25rem",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "14px",
              }}
            >
              <label style={{ display: "flex", alignItems: "baseline", gap: "8px", fontSize: "0.88rem", fontWeight: 700, color: "#334155", marginBottom: "0.75rem" }}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    background: "#fff1f2",
                    color: "#a00030",
                    fontSize: "0.68rem",
                    fontWeight: 800,
                    flexShrink: 0,
                  }}
                >
                  {idx + 1}
                </span>
                <span>
                  {q.question} {q.mandatory && <span style={{ color: "#c0392b" }}>*</span>}
                </span>
              </label>

              {q.type === "RATING" && (
                <div style={{ paddingLeft: "28px" }}>
                  <StarRating value={answers[q.id] || 0} onChange={(v) => setAnswer(q.id, v)} size={24} />
                </div>
              )}

              {q.type === "YES_NO" && (
                <div style={{ paddingLeft: "28px" }}>
                  <YesNoToggle value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />
                </div>
              )}

              {q.type === "TEXT" && (
                <div style={{ paddingLeft: "28px" }}>
                  <textarea
                    value={answers[q.id] || ""}
                    onChange={(e) => setAnswer(q.id, e.target.value)}
                    rows={3}
                    placeholder="Your answer..."
                    style={{
                      width: "100%",
                      padding: "0.65rem 0.85rem",
                      border: "1.5px solid #e2e8f0",
                      borderRadius: "10px",
                      fontSize: "0.88rem",
                      boxSizing: "border-box",
                      resize: "vertical",
                      background: "#fff",
                      outline: "none",
                      fontFamily: "inherit",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#a00030")}
                    onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                  />
                </div>
              )}
            </div>
          ))}

          {/* Additional comments */}
          <div style={{ marginBottom: "1.75rem" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.85rem", fontWeight: 700, color: "#334155", marginBottom: "0.5rem" }}>
              <FaRegCommentDots size={13} color="#94a3b8" />
              Additional Comments <span style={{ color: "#94a3b8", fontWeight: 500 }}>(optional)</span>
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              placeholder="Anything else you'd like to share..."
              style={{
                width: "100%",
                padding: "0.75rem 0.9rem",
                border: "1.5px solid #e2e8f0",
                borderRadius: "10px",
                fontSize: "0.9rem",
                boxSizing: "border-box",
                resize: "vertical",
                outline: "none",
                fontFamily: "inherit",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#a00030")}
              onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: "100%",
              background: "linear-gradient(135deg,#a00030,#e11d48)",
              border: "none",
              color: "#fff",
              padding: "0.9rem",
              borderRadius: "12px",
              fontWeight: 800,
              fontSize: "0.98rem",
              cursor: "pointer",
              opacity: submitting ? 0.7 : 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              boxShadow: "0 8px 20px rgba(160,0,48,0.25)",
              transition: "transform 0.1s, opacity 0.15s",
            }}
            onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.98)")}
            onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            {submitting && <CircularProgress size={16} style={{ color: "#fff" }} />}
            {submitting ? "Submitting…" : "Submit Feedback"}
          </button>
        </form>
      </div>

      <p style={{ textAlign: "center", fontSize: "0.75rem", color: "#94a3b8", marginTop: "1.25rem" }}>
        Your response is anonymous unless you provide your name.
      </p>
    </div>
  );
}